/**
 * Constants for entity status validation and categorization.
 * These constants mirror the backend EntityStatusConstants to ensure consistency.
 */

// Active/Valid statuses - entities in good standing
export const ACTIVE_STATUSES = [
    "active",
    "current active",
    "good",
    "exists",
    "in existence",
    "active existing",
    "active current",
    "current active",
    "active good standing",
    "active and in good standing",
    "goodstanding",
    "revived",
];

// Not valid statuses - entities not in good standing or terminated
export const NOT_VALID_STATUSES = [
    "dead",
    "dissolved",
    "inactive",
    "delinquent",
    "pending termination",
    "terminated",
    "administratively dissolved name protection",
    "admin. dissolved",
    "admin dissolved",
    "suspended",
    "revoked",
    "failed",
    "term canceled",
    "cancelled",
    "forfeited",
    "forfeited ftb",
    "forfeited existence",
    "forfeited ftb/sos",
    "admin diss/cancel repor",
    "expired failure to file renewal",
    "active not in good standing",
    "not in good standing",
    "not good standing",
    "involuntary dissolution",
    "inactive dissolved (administrative)",
    "inactive - cancelled",
    "inactive action by secretary of state",
    "inactive dissolution",
    "inactive merged",
    "inactive revoked (administrative)",
    "inactive withdrawn consolidated inactive",
    "inactive administratively dissolved (tax)",
    "inactive terminated",
    "inactive involuntary",
    "dissolved not in good standing",
    "revoked: operation of law",
    "revoked final",
    "term/cancel",
    "in existence but not in good standing",
    "expired merged",
    "merger",
    "merged",
    "failure to pay fee",
    "withdrawn",
];

// Single-word statuses considered "bad"/invalid. For multi-word phrases, choose the key single word.
export const NOT_VALID_SINGLE_WORDS = [
    "dissolved",
    "inactive",
    "noncompliant",
    "non-compliant",
    "noncompliance",
    "non-compliance",
    "delinquent",
    "termination",   // from "pending termination"
    "terminated",
    "suspended",
    "revoked",
    "failed",
    "canceled",      // from "term canceled", "cancelled", "term/cancel"
    "forfeited",
    "expired",
    "merger",        // from "expired merged", "merger"
    "merged",
    "withdrawn"
];

// No status determination - statuses that don't provide clear validity info
export const NO_STATUS_STATUSES = [
    "n/a",
    "converted",
    "incorporated",
    "incorporated/qualified",
    "organized",
    "excused",
];

/**
 * Validates if a company status is considered valid/active.
 * Mirrors the backend is_valid_company_status function logic.
 *
 * @param {string} status - The company status to validate
 * @returns {boolean} - True if the status is valid/active, false if invalid/inactive
 */
export const isValidCompanyStatus = (status) => {
    if (!status) {
        return true;
    }

    // Normalize status: lowercase and join split words (matches backend logic)
    const normalizedStatus = status.toLowerCase().split(' ').join(' ');

    // Check if it's an exact match or contains any not valid statuses
    if (NOT_VALID_STATUSES.some(invalidStatus =>
        invalidStatus === normalizedStatus || normalizedStatus.includes(invalidStatus)
    )) {
        return false;
    }

    // Additionally, check if it contains any not valid single word (substring match)
    if (NOT_VALID_SINGLE_WORDS.some(word =>
        normalizedStatus.includes(word)
    )) {
        return false;
    }

    // Check if it contains any no status determination keywords
    if (NO_STATUS_STATUSES.some(noStatusKeyword => normalizedStatus.includes(noStatusKeyword))) {
        return true;
    }

    // Check if it's an exact match for active statuses
    if (ACTIVE_STATUSES.some(activeStatus => activeStatus === normalizedStatus)) {
        return true;
    }

    // Default to true (valid) for unknown statuses
    return true;
};

/**
 * Determines if a company is inactive (inverse of isValidCompanyStatus).
 * This is the function that should be used in UI components for consistency.
 *
 * @param {object|string} entity - The entity object or status string
 * @param {string} inputStatus - Optional status override
 * @returns {boolean} - True if the entity is inactive, false if active
 */
export const isInactive = (entity, inputStatus = null) => {
    // Only consider inactive if it's a company
    if (entity && typeof entity === 'object' && entity.type !== 'COMPANY') {
        return false;
    }

    // Get the status from various possible sources
    let status = inputStatus;
    if (!status && entity) {
        if (typeof entity === 'string') {
            status = entity;
        } else {
            status = entity.data?.status || entity.status;
        }
    }

    // Use the inverse of isValidCompanyStatus for consistency
    return !isValidCompanyStatus(status);
};
